import { useEffect, useRef, useState } from "react";
import { AlertTriangle, ImageUp, Trash2, X } from "lucide-react";

import BrandLogo from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { superAdminApi, type Org } from "./session";

/** Mirrors RESERVED_SLUGS in app/routers/orgs.py — the API rejects these too. */
const RESERVED_SLUGS = new Set([
  "admin", "advisor", "api", "assets", "auth", "bl-advisor", "buyer",
  "choose-plan", "coming-soon", "favorites", "health", "legal",
  "legal-onboarding", "listings", "login", "logout", "my-interests",
  "profile", "register", "seller", "sign-in", "sign-nda", "sign-up",
  "static", "super-admin", "support", "unsubscribe", "upcoming-modules",
  "uploads",
]);

const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
/** Matches MAX_IMAGE_SIZE in app/services/image_validation.py. */
const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const ACCEPTED_LOGO_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

type FormState = {
  name: string;
  slug: string;
  headline: string;
  subheadline: string;
  description: string;
  website_url: string;
  tier_names: string;
};

const EMPTY: FormState = {
  name: "",
  slug: "",
  headline: "",
  subheadline: "",
  description: "",
  website_url: "",
  tier_names: "",
};

function toForm(org: Org): FormState {
  return {
    name: org.name,
    slug: org.slug,
    headline: org.headline ?? "",
    subheadline: org.subheadline ?? "",
    description: org.description ?? "",
    website_url: org.website_url ?? "",
    tier_names: org.tier_names.join(", "),
  };
}

/**
 * Create/edit dialog for an organization.
 *
 * One component for both modes: the fields are identical, and keeping them in
 * one place is what stops the create form and the edit form from drifting apart.
 * `org === null` means create.
 *
 * Logo handling differs between the two, because the upload endpoint needs an
 * org id. On edit the file uploads immediately; on create the file is held
 * until the org exists, then uploaded straight after.
 */
export default function OrgFormModal({
  org,
  existingSlugs,
  onClose,
  onSaved,
}: {
  org: Org | null;
  /** Slugs already in use, for a client-side collision check. */
  existingSlugs: string[];
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const isEdit = org !== null;
  const isDefaultOrg = org?.is_default ?? false;

  const [form, setForm] = useState<FormState>(org ? toForm(org) : EMPTY);
  const [slugTouched, setSlugTouched] = useState(isEdit);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(org?.logo_url ?? null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  // Esc closes, matching every other dialog in the app.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Object URLs for the local preview must be revoked or they leak for the
  // lifetime of the page.
  useEffect(() => {
    if (!logoFile) return;
    const url = URL.createObjectURL(logoFile);
    setLogoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [logoFile]);

  const setField = (key: keyof FormState, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    setError("");
  };

  const pickLogo = (file: File | undefined) => {
    if (!file) return;
    if (!ACCEPTED_LOGO_TYPES.includes(file.type)) {
      setError("Logo must be a JPEG, PNG, GIF or WebP image.");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setError("Logo is too large. Maximum size is 2 MB.");
      return;
    }
    setError("");
    setLogoFile(file);
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const validate = (): string | null => {
    if (!form.name.trim()) return "Organization name is required.";
    // The default org is served at "/", so its slug is never a URL.
    if (isDefaultOrg) return null;
    const slug = form.slug.trim().toLowerCase();
    if (!slug) return "URL slug is required.";
    if (!SLUG_RE.test(slug))
      return "Slug must be lowercase letters, numbers and single hyphens (e.g. 'acme-capital').";
    if (RESERVED_SLUGS.has(slug)) return `'/${slug}' is reserved by an existing page.`;
    if (existingSlugs.includes(slug) && slug !== org?.slug) return `'/${slug}' is already taken.`;
    return null;
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const issue = validate();
    if (issue) {
      setError(issue);
      return;
    }
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        name: form.name.trim(),
        headline: form.headline.trim() || null,
        subheadline: form.subheadline.trim() || null,
        description: form.description.trim() || null,
        website_url: form.website_url.trim() || null,
        tier_names: form.tier_names
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      };
      // The default org's slug isn't a URL, so it's never sent — the API
      // rejects changing it.
      if (!isDefaultOrg) body.slug = form.slug.trim().toLowerCase();

      let saved: Org;
      if (isEdit) {
        // Clearing an existing logo is a field update, not an upload.
        if (!logoPreview && org.logo_url) body.logo_url = null;
        saved = await superAdminApi.patch<Org>(`/orgs/${org.id}`, body);
      } else {
        saved = await superAdminApi.post<Org>("/orgs", body);
      }

      // Upload last: it needs an org id, which on create only exists now.
      if (logoFile) {
        saved = await superAdminApi.upload<Org>(`/orgs/${saved.id}/logo`, logoFile);
      }

      onSaved(
        isEdit
          ? `${saved.name} updated`
          : `${saved.name} created — live at ${saved.url_path}`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save organization");
    } finally {
      setSaving(false);
    }
  };

  const slugChanged = isEdit && !isDefaultOrg && form.slug.trim().toLowerCase() !== org.slug;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 px-4 py-8 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-2xl rounded-2xl border border-border/80 bg-card shadow-xl">
        <div className="flex items-center justify-between border-b border-border/60 px-6 py-4">
          <h2 className="text-lg font-bold text-foreground">
            {isEdit ? `Edit ${org.name}` : "New organization"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground transition-colors hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-5 px-6 py-5">
          {/* ── Logo ──────────────────────────────────────────────── */}
          <div className="space-y-1.5">
            <Label>Logo</Label>
            {isDefaultOrg ? (
              <div className="flex items-center gap-3 rounded-xl border border-border/60 bg-muted/30 p-3">
                <BrandLogo className="h-9 w-auto" />
                <p className="text-xs text-muted-foreground">
                  The default organization always uses the BusinessLocating brand logo.
                </p>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-border/60 bg-muted/30">
                  {logoPreview ? (
                    <img src={logoPreview} alt="" className="h-full w-full object-contain p-1.5" />
                  ) : (
                    <ImageUp className="h-5 w-5 text-muted-foreground/60" />
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <input
                    ref={fileRef}
                    type="file"
                    accept={ACCEPTED_LOGO_TYPES.join(",")}
                    className="hidden"
                    onChange={(e) => pickLogo(e.target.files?.[0])}
                  />
                  <Button type="button" variant="outline" onClick={() => fileRef.current?.click()}>
                    <ImageUp className="h-4 w-4" />
                    {logoPreview ? "Replace logo" : "Upload logo"}
                  </Button>
                  {logoPreview && (
                    <Button type="button" variant="outline" onClick={removeLogo}>
                      <Trash2 className="h-4 w-4" />
                      Remove
                    </Button>
                  )}
                </div>
                <p className="w-full text-xs text-muted-foreground">
                  Shown in the navigation bar on this organization's page. PNG, JPEG, GIF or WebP,
                  up to 2 MB. SVG isn't accepted.
                </p>
              </div>
            )}
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="org-name">Organization name</Label>
              <Input
                id="org-name"
                value={form.name}
                placeholder="eCashflow Acquisitions"
                onChange={(e) => {
                  setField("name", e.target.value);
                  if (!slugTouched) setForm((f) => ({ ...f, slug: slugify(e.target.value) }));
                }}
                required
                autoFocus
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="org-slug">URL slug</Label>
              {isDefaultOrg ? (
                <div className="flex h-10 items-center rounded-lg border border-border/60 bg-muted/30 px-3 text-sm text-muted-foreground">
                  Served at the site root — no URL slug
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-muted-foreground">/</span>
                    <Input
                      id="org-slug"
                      value={form.slug}
                      placeholder="ecfa"
                      onChange={(e) => {
                        setSlugTouched(true);
                        setField("slug", e.target.value.toLowerCase());
                      }}
                      required
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Reached at{" "}
                    <span className="font-semibold text-foreground">/{form.slug || "slug"}</span>
                  </p>
                </>
              )}
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="org-headline">Headline</Label>
              <Input
                id="org-headline"
                value={form.headline}
                placeholder="Private, off-market deal flow beyond traditional listing marketplaces."
                onChange={(e) => setField("headline", e.target.value)}
              />
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="org-subheadline">Subheadline</Label>
              <Textarea
                id="org-subheadline"
                rows={2}
                value={form.subheadline}
                placeholder="One or two sentences under the headline."
                onChange={(e) => setField("subheadline", e.target.value)}
              />
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="org-description">Description</Label>
              <Textarea
                id="org-description"
                rows={3}
                value={form.description}
                placeholder="Longer supporting paragraph shown below the hero."
                onChange={(e) => setField("description", e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="org-website">Website URL</Label>
              <Input
                id="org-website"
                type="url"
                value={form.website_url}
                placeholder="https://www.example.com/"
                onChange={(e) => setField("website_url", e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="org-tiers">Plan names</Label>
              <Input
                id="org-tiers"
                value={form.tier_names}
                placeholder="ECFA Explorer, ECFA Premium"
                onChange={(e) => setField("tier_names", e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Comma-separated, matched against existing tier names.
              </p>
            </div>
          </div>

          {slugChanged && (
            <div className="flex items-start gap-2.5 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
              <p className="text-xs leading-5 text-foreground/80">
                Changing the slug moves this organization from{" "}
                <span className="font-semibold">/{org.slug}</span> to{" "}
                <span className="font-semibold">/{form.slug}</span>. Every link already shared —
                emails, bookmarks, ads — will stop working.
              </p>
            </div>
          )}

          {error && <p className="text-sm font-medium text-destructive">{error}</p>}

          <div className="flex gap-2 border-t border-border/60 pt-4">
            <Button type="submit" disabled={saving}>
              {saving ? "Saving..." : isEdit ? "Save changes" : "Create Organization"}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
